import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardBody, Row, Col, Input, FormGroup, Label, Badge, FormFeedback } from 'reactstrap'
import { Truck, CreditCard, Check } from 'react-feather'
import CountrySelect from '../../addresses/CountrySelect'
import { supportedCountries } from '../../../configs/supportedCountries'

export const SHIPPING_PAYMENT_METHOD = {
  STANDARD: 'standard',
  THIRD_PARTY_BILLING: 'third_party_billing'
}

export const CARRIER_OPTIONS = {
  ups: { label: 'UPS', value: 'ups' },
  fedex: { label: 'FedEx', value: 'fedex' },
  dhl: { label: 'DHL', value: 'dhl' }
}

/**
 * ShippingPaymentMethodSelector - Allows users to choose between standard shipping rates
 * or billing to their own carrier account (third party billing)
 */
const ShippingPaymentMethodSelector = ({
  shippingPaymentMethod,
  onShippingPaymentMethodChange,
  thirdPartyData,
  onThirdPartyDataChange
}) => {
  const currentLocation = useSelector(state => state.storeViews.current)
  const portal = useSelector(state => state.portal)

  const [formErrors, setFormErrors] = useState({
    carrierCode: null,
    accountNumber: null,
    zipCode: null
  })

  const {
    third_party_billing_enabled: enabled,
    third_party_billing_carriers: carriers = []
  } = currentLocation || {}

  // Get dynamic postcode label based on selected country
  const countries = supportedCountries(portal?.supported_countries)
  const selectedCountry = countries.find(c => c.country_code === thirdPartyData?.countryCode) || {
    labels: { postcode: 'Postal Code' }
  }

  // Don't render if third party billing is not enabled
  if (!enabled) {
    return null
  }

  // Required field indicator
  const requiredAsterisk = <span className="text-danger">*</span>

  const validateField = (field, value, label) => {
    if (!value || value.trim() === '') {
      return `${label} is required`
    }
    return null
  }

  const handleCarrierSelect = carrierCode => {
    setFormErrors(prev => ({ ...prev, carrierCode: null }))
    onThirdPartyDataChange({
      ...thirdPartyData,
      carrierCode
    })
  }

  const handleInputChange = (field, value) => {
    onThirdPartyDataChange({
      ...thirdPartyData,
      [field]: value
    })
  }

  const handleBlur = (field, value, label) => {
    const error = validateField(field, value, label)
    setFormErrors(prev => ({ ...prev, [field]: error }))
  }

  const handleCountryChange = country => {
    onThirdPartyDataChange({
      ...thirdPartyData,
      countryCode: country?.country_code || 'US'
    })
  }

  const isStandardSelected = shippingPaymentMethod === SHIPPING_PAYMENT_METHOD.STANDARD
  const isThirdPartySelected = shippingPaymentMethod === SHIPPING_PAYMENT_METHOD.THIRD_PARTY_BILLING

  return (
    <div className="shipping-payment-selector mb-4">
      {/* Standard Shipping Option */}
      <Card
        className={`mb-2 shipping-payment-card ${isStandardSelected ? 'border-primary' : ''}`}
        onClick={() => onShippingPaymentMethodChange(SHIPPING_PAYMENT_METHOD.STANDARD)}
        style={{ cursor: 'pointer', borderWidth: isStandardSelected ? '2px' : '1px' }}
      >
        <CardBody className="py-3">
          <Row className="align-items-center">
            <Col xs="auto" className="pr-0">
              <div
                className={`d-flex align-items-center justify-content-center rounded-circle ${
                  isStandardSelected ? 'bg-primary text-white' : 'bg-light text-muted'
                }`}
                style={{ width: 40, height: 40 }}
              >
                <Truck size={20} />
              </div>
            </Col>
            <Col>
              <div className="d-flex align-items-center">
                <strong className="mb-0">Standard Shipping Rates</strong>
              </div>
              <small className="text-muted">Pay standard rates through our checkout</small>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Third Party Billing Option */}
      <Card
        className={`shipping-payment-card ${isThirdPartySelected ? 'border-primary' : ''}`}
        style={{ cursor: 'pointer', borderWidth: isThirdPartySelected ? '2px' : '1px' }}
      >
        <CardBody
          className="py-3"
          onClick={() => onShippingPaymentMethodChange(SHIPPING_PAYMENT_METHOD.THIRD_PARTY_BILLING)}
        >
          <Row className="align-items-center">
            <Col xs="auto" className="pr-0">
              <div
                className={`d-flex align-items-center justify-content-center rounded-circle ${
                  isThirdPartySelected ? 'bg-primary text-white' : 'bg-light text-muted'
                }`}
                style={{ width: 40, height: 40 }}
              >
                <CreditCard size={20} />
              </div>
            </Col>
            <Col>
              <div className="d-flex align-items-center">
                <strong className="mb-0">Third Party Billing</strong>
                <Badge color="secondary" className="ml-2" pill>
                  Optional
                </Badge>
              </div>
              <small className="text-muted">Use your negotiated carrier rates and bill to your account</small>
            </Col>
          </Row>
        </CardBody>

        {/* Third Party Billing Form - only show when selected */}
        {isThirdPartySelected && (
          <CardBody className="pt-0 border-top bg-light">
            <div className="pt-3">
              {/* Carrier Selection */}
              {carriers.length > 0 && (
                <FormGroup className="mb-3">
                  <Label style={{ fontSize: '0.875rem' }} className="text-muted mb-2">
                    Select Carrier {requiredAsterisk}
                  </Label>
                  <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                    {carriers.map(carrierKey => {
                      const carrierOption = CARRIER_OPTIONS[carrierKey] || { label: carrierKey, value: carrierKey }
                      const isSelected = thirdPartyData?.carrierCode === carrierOption.value
                      return (
                        <button
                          key={carrierOption.value}
                          type="button"
                          className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleCarrierSelect(carrierOption.value)}
                          style={{ minWidth: 70 }}
                        >
                          {isSelected && <Check size={12} className="mr-1" />}
                          {carrierOption.label}
                        </button>
                      )
                    })}
                  </div>
                  {formErrors.carrierCode && <span className="text-danger small">{formErrors.carrierCode}</span>}
                </FormGroup>
              )}

              {/* Account Number */}
              <FormGroup className="mb-3">
                <Label style={{ fontSize: '0.875rem' }} className="text-muted mb-2">
                  Account Number {requiredAsterisk}
                </Label>
                <Input
                  type="text"
                  placeholder="Enter your carrier account number"
                  value={thirdPartyData?.accountNumber || ''}
                  onChange={e => handleInputChange('accountNumber', e.target.value)}
                  onBlur={e => handleBlur('accountNumber', e.target.value, 'Account Number')}
                  invalid={!!formErrors.accountNumber}
                />
                <FormFeedback>{formErrors.accountNumber}</FormFeedback>
              </FormGroup>

              {/* ZIP Code and Country */}
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-3 mb-md-0">
                    <Label style={{ fontSize: '0.875rem' }} className="text-muted mb-2">
                      {selectedCountry.labels.postcode} {requiredAsterisk}
                    </Label>
                    <Input
                      type="text"
                      placeholder={`Enter ${selectedCountry.labels.postcode.toLowerCase()}`}
                      value={thirdPartyData?.zipCode || ''}
                      onChange={e => handleInputChange('zipCode', e.target.value)}
                      onBlur={e => handleBlur('zipCode', e.target.value, selectedCountry.labels.postcode)}
                      invalid={!!formErrors.zipCode}
                    />
                    <FormFeedback>{formErrors.zipCode}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label style={{ fontSize: '0.875rem' }} className="text-muted mb-2">
                      Country {requiredAsterisk}
                    </Label>
                    <CountrySelect
                      countryCode={thirdPartyData?.countryCode || 'US'}
                      handleChange={handleCountryChange}
                      menuPlacement="top"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </CardBody>
        )}
      </Card>
    </div>
  )
}

export default ShippingPaymentMethodSelector
